import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';

import useDebounce from '~/hooks/useDebounce';

import styles from './Campaign.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { ICampaign } from '~/utils/interface/campaign';

import { handleGetCampaign, handleGetCampaigns } from '~/thunks/campaign/campaignThunk';
import { campaignActions } from '~/thunks/campaign/campaignSlice';
import campaignApi from '~/apis/campaign';
import { IPayloadDeletePriceComponent } from '~/utils/interface/estimate';
import CampaignList from '~/components/campaigns/campaign-list/CampaignList';
import EditCampaignDialog from '~/components/campaigns/edit-campaign-dialog/EditCampaignDialog';

const cx = classNames.bind(styles);

const Campaign = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const permissions = usePermission();
  const { t } = useTranslation(['admin/campaign', 'admin/component']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.campaign
  );
  //#endregion Selector

  //#region Declare State
  const [searchInput, setSearchInput] = useState<string>('');
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<ICampaign>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        campaignActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetCampaigns(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetCampaign(id)).unwrap();

      setEditData(response);
      setEditDialogMode(DIALOG_MODE_EDIT);
      setOpenEditDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(campaignActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(campaignActions.changeMenuSelectId(undefined));
    dispatch(campaignActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedCampaigns = useCallback(async () => {
    let payloadDeleteCampaigns: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await campaignApi.delete(payloadDeleteCampaigns);
      notify('success', t('deleteSuccessMessage'));
      dispatch(campaignActions.changeNeedRefreshData(true));
      dispatch(campaignActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);
  //#endregion Handle Function

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions[PERMISSIONS.campaign_delete] && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete', { ns: 'admin/common' })}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions[PERMISSIONS.campaign_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditCampaignDialog open={openEditDialog} setOpen={handleSetOpen} mode={editDialogMode} data={editData} />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <CampaignList onOpenEdit={handleOpenEdit} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedCampaigns}
      />
    </Box>
  );
};

export default Campaign;
